/* Meubel31 product page behaviour. Vanilla JS, no dependencies. */
(() => {
  const PREVIEW = window.M31_PREVIEW || null;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const money = (cents) => {
    const [whole, frac] = (Number(cents || 0) / 100).toFixed(2).split('.');
    return `€${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${frac}`;
  };
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /* ---------- Delivery: one source for every place the promise is shown ---------- */

  const deliveryText = (value) => window.M31Delivery.deliveryText(value);

  function renderDelivery(root = document) {
    root.querySelectorAll('[data-m31-delivery]').forEach((el) => {
      const text = deliveryText(el.dataset.window);
      const target = el.querySelector('[data-m31-delivery-text]');
      if (text && target) target.textContent = text;
    });
  }

  /* ---------- Cart API (Shopify Ajax API, or an in-memory cart in the preview) ---------- */

  const previewCart = { items: [] };
  const cartApi = {
    async add(items) {
      if (PREVIEW) {
        items.forEach((it) => {
          const line = previewCart.items.find((l) => l.id === it.id);
          if (line) line.quantity += it.quantity;
          else previewCart.items.push({ ...PREVIEW.variants[it.id], id: it.id, quantity: it.quantity });
        });
        return this.get();
      }
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || body.message || 'Toevoegen lukte niet, probeer het opnieuw.');
      }
      return this.get();
    },
    // Lines are changed by key, so a line with properties (or added by an app) is never mixed up with another.
    async change(key, quantity) {
      if (PREVIEW) {
        previewCart.items = previewCart.items.map((l) => (String(l.id) === String(key) ? { ...l, quantity } : l)).filter((l) => l.quantity > 0);
        return this.get();
      }
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }),
      });
      if (!res.ok) throw new Error('Aanpassen lukte niet, probeer het opnieuw.');
      return res.json();
    },
    async get() {
      if (PREVIEW) {
        const items = previewCart.items.map((l) => ({ ...l, key: String(l.id), variant_id: l.id, final_line_price: l.price * l.quantity }));
        return { items, total_price: items.reduce((n, l) => n + l.final_line_price, 0), item_count: items.reduce((n, l) => n + l.quantity, 0) };
      }
      return (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
    },
    checkoutUrl(cart) {
      if (PREVIEW) return `${PREVIEW.store}/cart/${cart.items.map((l) => `${l.variant_id}:${l.quantity}`).join(',')}`;
      return `${window.Shopify?.routes?.root || '/'}checkout`;
    },
  };

  /* ---------- Shared scroll loop (no IntersectionObserver: fast flings skip frames) ---------- */

  const scrollTasks = new Set();
  let ticking = false;
  const runScrollTasks = () => { ticking = false; scrollTasks.forEach((fn) => fn()); };
  const requestTick = () => { if (!ticking) { ticking = true; requestAnimationFrame(runScrollTasks); } };
  window.addEventListener('scroll', requestTick, { passive: true });
  window.addEventListener('resize', requestTick);

  function initReveals() {
    if (reduceMotion) return;
    const items = [...document.querySelectorAll('.m31 [data-m31-reveal]')];
    if (!items.length) return;
    document.documentElement.classList.add('m31-reveal-on');
    // Stagger siblings in the same row
    items.forEach((el) => {
      const siblings = [...el.parentElement.children].filter((c) => c.hasAttribute('data-m31-reveal'));
      const i = siblings.indexOf(el);
      if (i > 0) el.style.setProperty('--m31-delay', `${Math.min(i, 5) * 70}ms`);
    });
    const check = () => {
      const limit = window.innerHeight * 0.92;
      items.forEach((el) => {
        if (el.classList.contains('is-in')) return;
        if (el.getBoundingClientRect().top < limit) el.classList.add('is-in'); // also covers everything already scrolled past
      });
    };
    scrollTasks.add(check);
    check();
    // Failsafe: nothing may stay hidden if the loop is throttled (iOS low power mode)
    setTimeout(() => items.forEach((el) => el.classList.add('is-in')), 4000);
  }

  function initAutoplayVideos() {
    const videos = [...document.querySelectorAll('.m31 video[data-m31-autoplay]')];
    const check = () => {
      videos.forEach((video) => {
        const r = video.getBoundingClientRect();
        const near = r.bottom > -100 && r.top < window.innerHeight + 200;
        if (near) { loadVideo(video); if (video.paused && !reduceMotion) video.play().catch(() => {}); }
        else if (!video.paused) video.pause();
      });
    };
    if (videos.length) { scrollTasks.add(check); check(); }
  }

  function loadVideo(video) {
    if (video.dataset.loaded) return;
    video.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; });
    video.dataset.loaded = '1';
    video.load();
  }

  /* ---------- Product element ---------- */

  class M31Product extends HTMLElement {
    connectedCallback() {
      this.price = Number(this.dataset.price);
      this.minQty = Number(this.dataset.minQty || 1);
      this.unitMany = this.dataset.unitMany || 'stoelen';
      this.error = this.querySelector('[data-m31-error]');
      this.addonPrice = Number(this.dataset.addonPrice || 0);
      this.qtyInput = this.querySelector('[data-m31-qty]');
      this.formQty = this.querySelector('[data-m31-form-qty]');
      this.addon = this.querySelector('[data-m31-addon]');
      this.atc = this.querySelector('[data-m31-atc]');
      this.sticky = this.querySelector('[data-m31-sticky]');
      this.drawer = this.querySelector('[data-m31-drawer]');

      this.initGallery();
      this.initQuantity();
      this.initForm();
      this.initSticky();
      this.initDrawer();
      renderDelivery(this);

      this.querySelectorAll('[data-m31-scroll]').forEach((a) => a.addEventListener('click', (e) => {
        const target = document.querySelector(a.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }));
    }

    /* Gallery */
    initGallery() {
      const track = this.querySelector('[data-m31-track]');
      if (!track) return;
      const slides = [...track.querySelectorAll('[data-m31-slide]')];
      const current = this.querySelector('[data-m31-current]');
      const prev = this.querySelector('[data-m31-prev]');
      const next = this.querySelector('[data-m31-next]');
      const thumbs = this.querySelector('[data-m31-thumbs]');

      slides.forEach((slide, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', `Toon ${slide.dataset.type === 'video' ? 'video' : 'foto'} ${i + 1}`);
        b.innerHTML = `<img src="${slide.dataset.thumb}" alt="" loading="lazy">${slide.dataset.type === 'video' ? '<span class="m31-thumb-play"><svg class="m31-icon m31-icon--fill" viewBox="0 0 24 24"><path d="M8 5.5v13l10.5-6.5z"/></svg></span>' : ''}`;
        b.addEventListener('click', () => go(i));
        thumbs?.appendChild(b);
      });

      const go = (i) => {
        const slide = slides[Math.max(0, Math.min(slides.length - 1, i))];
        track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      let active = -1;
      const update = () => {
        const x = track.scrollLeft;
        let idx = 0;
        slides.forEach((s, i) => { if (s.offsetLeft - track.offsetLeft - 4 <= x) idx = i; });
        if (x + track.clientWidth >= track.scrollWidth - 4) {
          // at the end: pick the first slide that is fully visible
          idx = slides.findIndex((s) => s.offsetLeft - track.offsetLeft >= x - 4);
        }
        if (idx === active) return;
        active = idx;
        if (current) current.textContent = String(idx + 1);
        thumbs?.querySelectorAll('button').forEach((b, i) => b.classList.toggle('is-active', i === idx));
        if (prev) prev.disabled = x <= 4;
        if (next) next.disabled = x + track.clientWidth >= track.scrollWidth - 4;
        // Play the videos that are in view, pause the rest
        const view = track.getBoundingClientRect();
        slides.forEach((s) => {
          const video = s.querySelector('video');
          if (!video) return;
          const r = s.getBoundingClientRect();
          const visible = r.right > view.left + 20 && r.left < view.right - 20;
          if (visible) { loadVideo(video); if (!reduceMotion) video.play().catch(() => {}); }
          else video.pause();
        });
      };
      let raf = 0;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', () => { active = -1; update(); });
      prev?.addEventListener('click', () => go(active - 1));
      next?.addEventListener('click', () => go(active + 1));
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(active + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(active - 1); }
      });
      update();
    }

    /* Quantity, sets and add-on */
    initQuantity() {
      this.querySelectorAll('[data-m31-set]').forEach((btn) => btn.addEventListener('click', () => this.setQty(Number(btn.dataset.m31Set))));
      this.querySelectorAll('[data-m31-step]').forEach((btn) => btn.addEventListener('click', () => this.setQty(this.qty + Number(btn.dataset.m31Step))));
      this.qtyInput?.addEventListener('change', () => this.setQty(parseInt(this.qtyInput.value, 10)));
      this.addon?.addEventListener('change', () => this.updateTotals());
      this.setQty(Number(this.qtyInput?.value || this.minQty));
    }

    setQty(n) {
      this.qty = Math.max(this.minQty, Number.isFinite(n) ? n : this.minQty);
      if (this.qtyInput) this.qtyInput.value = this.qty;
      if (this.formQty) this.formQty.value = this.qty;
      this.querySelectorAll('[data-m31-set]').forEach((btn) => {
        const on = Number(btn.dataset.m31Set) === this.qty;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-checked', String(on));
      });
      this.updateTotals();
    }

    updateTotals() {
      const chairs = this.price * this.qty;
      const addonTotal = this.addonPrice * this.qty;
      const total = chairs + (this.addon?.checked ? addonTotal : 0);
      this.querySelectorAll('[data-m31-total], [data-m31-sticky-total]').forEach((el) => { el.textContent = money(total); });
      this.querySelectorAll('[data-m31-sticky-qty]').forEach((el) => { el.textContent = this.qty; });
      this.querySelectorAll('[data-m31-addon-total]').forEach((el) => { el.textContent = `+ ${money(addonTotal)}`; });
      this.querySelector('[data-m31-sticky-atc]')?.setAttribute('aria-label', `${this.qty} ${this.unitMany} in winkelwagen, ${money(total)}`);
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message || '';
      this.error.hidden = !message;
    }

    /* Add to cart */
    initForm() {
      const form = this.querySelector('[data-m31-form]');
      form?.addEventListener('submit', (e) => { e.preventDefault(); this.addToCart(); });
      this.querySelector('[data-m31-sticky-atc]')?.addEventListener('click', () => this.addToCart());
    }

    async addToCart() {
      if (this.busy) return;
      const items = [{ id: Number(this.dataset.variantId), quantity: this.qty }];
      if (this.addon?.checked && this.dataset.addonId) items.push({ id: Number(this.dataset.addonId), quantity: this.qty });
      this.busy = true;
      this.showError('');
      this.atc?.setAttribute('aria-busy', 'true');
      try {
        const cart = await cartApi.add(items);
        document.dispatchEvent(new CustomEvent('m31:cart-added', { detail: { items, cart } }));
        this.openDrawer(cart, true);
      } catch (err) {
        this.showError(err.message);
      } finally {
        this.busy = false;
        this.atc?.removeAttribute('aria-busy');
      }
    }

    /* Sticky bar */
    initSticky() {
      if (!this.sticky || !this.atc) return;
      const check = () => {
        const r = this.atc.getBoundingClientRect();
        const footer = document.querySelector('[data-m31-footer]');
        const footerIn = footer && footer.getBoundingClientRect().top < window.innerHeight;
        const show = r.bottom < 0 && !footerIn;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', String(!show));
        this.sticky.querySelector('button')?.setAttribute('tabindex', show ? '0' : '-1');
        document.documentElement.classList.toggle('m31-sticky-visible', show);
      };
      scrollTasks.add(check);
      check();
    }

    /* Drawer */
    initDrawer() {
      if (!this.drawer) return;
      this.drawer.querySelectorAll('[data-m31-close]').forEach((el) => el.addEventListener('click', () => this.closeDrawer()));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.closeDrawer(); });
      this.drawer.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-m31-line-step]');
        if (!btn || this.drawer.getAttribute('aria-busy') === 'true') return;
        this.drawer.setAttribute('aria-busy', 'true');
        try {
          const cart = await cartApi.change(btn.dataset.key, Number(btn.dataset.qty));
          this.renderDrawer(cart, false);
        } catch (err) {
          this.drawer.querySelector('[data-m31-drawer-error]')?.replaceChildren(err.message);
        } finally {
          this.drawer.removeAttribute('aria-busy');
        }
      });
    }

    renderDrawer(cart, justAdded) {
      const list = this.drawer.querySelector('[data-m31-lines]');
      const added = this.drawer.querySelector('[data-m31-added]');
      added.hidden = !justAdded;
      this.drawer.querySelector('[data-m31-drawer-error]')?.replaceChildren();
      const variantId = Number(this.dataset.variantId);
      list.innerHTML = cart.items.length
        ? cart.items.map((l) => {
          // Chairs from this page keep their minimum per colour: one step below it removes the line.
          const min = Number(l.variant_id) === variantId ? this.minQty : 1;
          const down = l.quantity - 1 < min ? 0 : l.quantity - 1;
          return `
          <li class="m31-line">
            <span class="m31-line__img">${l.image ? `<img src="${esc(l.image)}" alt="" width="72" height="88" loading="lazy">` : ''}</span>
            <div>
              <p class="m31-line__title">${esc(l.product_title || l.title)}</p>
              <p class="m31-line__meta">${money(l.final_price ?? l.price)} per stuk</p>
              <div class="m31-stepper">
                <button type="button" data-m31-line-step data-key="${esc(l.key)}" data-qty="${down}" aria-label="${down === 0 ? 'Verwijderen' : 'Eén minder'}">−</button>
                <input type="number" value="${l.quantity}" readonly aria-label="Aantal">
                <button type="button" data-m31-line-step data-key="${esc(l.key)}" data-qty="${l.quantity + 1}" aria-label="Eén meer">+</button>
              </div>
            </div>
            <span class="m31-line__price">${money(l.final_line_price)}</span>
          </li>`;
        }).join('')
        : '<li class="m31-line"><p class="m31-line__meta">Je winkelwagen is leeg.</p></li>';
      this.drawer.querySelector('[data-m31-cart-total]').textContent = money(cart.total_price);
      // Same rule as the checkout: free from the threshold, otherwise the checkout calculates it
      const shipping = this.drawer.querySelector('[data-m31-shipping]');
      const freeFrom = Number(shipping?.dataset.freeFrom || 0);
      if (shipping) shipping.textContent = !freeFrom || cart.total_price >= freeFrom ? 'Gratis' : `Gratis vanaf ${money(freeFrom).replace(',00', '')}`;
      const checkout = this.drawer.querySelector('[data-m31-checkout]');
      checkout.href = cartApi.checkoutUrl(cart);
      checkout.toggleAttribute('aria-disabled', !cart.items.length);
      renderDelivery(this.drawer);
      // Our own counters plus the theme's header and mobile toolbar counters (Avone: #CartCount / .site-header__cart-count)
      document.querySelectorAll('[data-m31-cart-count], #CartCount, .site-header__cart-count').forEach((el) => { el.textContent = cart.item_count; });
      // Lets the theme update its own cart icon (Avone listens for its own events, wired up at install).
      document.dispatchEvent(new CustomEvent('m31:cart-updated', { detail: { cart } }));
    }

    openDrawer(cart, justAdded) {
      this.renderDrawer(cart, justAdded);
      this.lastFocus = document.activeElement;
      this.drawer.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => this.drawer.classList.add('is-open')));
      document.documentElement.style.overflow = 'hidden';
      this.drawer.querySelector('.m31-drawer__panel').focus({ preventScroll: true });
    }

    closeDrawer() {
      this.drawer.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      setTimeout(() => { this.drawer.hidden = true; }, reduceMotion ? 0 : 450);
      this.lastFocus?.focus?.({ preventScroll: true });
    }
  }

  if (!customElements.get('m31-product')) customElements.define('m31-product', M31Product);

  const start = () => { renderDelivery(); initReveals(); initAutoplayVideos(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
